import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useStoreActions, useStoreState } from 'easy-peasy';

import Form from './Form';
import { clearPendingCognitoUser } from '../../utils/auth';
import { getAuthenticationProfile } from '../../store/auth';

const Container = styled.section`
  &&& {
    min-height: 100vh;
    .is-flex {
      justify-content: space-between;
    }
    .hero-body {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    @media (max-width: 768px) {
      .hero-body {
        padding: 1.5rem 1rem;
      }
    }
  }
`;

const FormCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 1.75rem 1.25rem;
    border-radius: 12px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const FooterTextWrapper = styled.div`
  padding: 1rem 1rem;
  background: #f2f2f2;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Login = ({ history }) => {
  const clearError = useStoreActions(state => state.auth.clearError);
  const isError = useStoreState(state => state.auth.isError);
  const isLoading = useStoreState(state => state.auth.loading);
  const login = useStoreActions(state => state.auth.logIn);
  const otpRequired = useStoreState(state => state.auth.otpRequired);
  const confirmOtp = useStoreActions(state => state.auth.confirmOtp);
  const resendOtp = useStoreActions(state => state.auth.resendOtp);
  const setOtpRequired = useStoreActions(state => state.auth.setOtpRequired);
  const setPendingUsername = useStoreActions(
    state => state.auth.setPendingUsername,
  );
  const setOtpAttempts = useStoreActions(state => state.auth.setOtpAttempts);
  const setLoading = useStoreActions(state => state.auth.setLoading);
  const setError = useStoreActions(state => state.auth.setError);
  const redirecting = useStoreState(state => state.auth.redirecting);
  const setRedirecting = useStoreActions(state => state.auth.setRedirecting);
  const rememberDevicePrompt = useStoreState(
    state => state.auth.rememberDevicePrompt,
  );
  const confirmTrustDevice = useStoreActions(
    state => state.auth.confirmTrustDevice,
  );
  const declineTrustDevice = useStoreActions(
    state => state.auth.declineTrustDevice,
  );
  const authenticationProfile = useStoreState(
    state => state.auth.authenticationProfile,
  );
  const { defaultPlace, defaultPlaceId } = useStoreState(state => state.menu);
  const { setDefaultPlaceId, setDefaultPlace } = useStoreActions(
    state => state.menu,
  );
  const setAuthenticationProfile = useStoreActions(
    state => state.auth.setAuthenticationProfile,
  );
  const updateAuthenticationProfile = useStoreActions(
    state => state.auth.updateAuthenticationProfile,
  );
  const [phoneRequired, setPhoneRequired] = React.useState(false);
  const [userWithoutPhoneLogin, setUserWithoutPhoneLogin] = React.useState(
    false,
  );
  const [onSubmitValues, setOnSubmitValues] = React.useState(null);

  if (defaultPlace) {
    setDefaultPlace(null);
  }

  if (defaultPlaceId) {
    setDefaultPlaceId(null);
  }

  useEffect(() => {
    clearError();
    setOtpRequired(false);
    setOtpAttempts(0);
    setRedirecting(false);
    setPendingUsername(null);
    clearPendingCognitoUser();
  }, []);

  return (
    <Container className="hero is-medium">
      <div className="hero-body">
        <div className="container has-text-centered">
          <div className="columns is-centered">
            <div className="column is-12-mobile is-8-tablet is-6-desktop is-5-widescreen is-4-fullhd">
              <FormCard>
                <Form
                  preEnteredEmail={onSubmitValues?.username || ''}
                  preEnteredPassword={onSubmitValues?.password || ''}
                  isError={isError}
                  isLoading={isLoading}
                  redirecting={redirecting}
                  otpRequired={otpRequired}
                  authenticationProfile={authenticationProfile}
                  rememberDevicePrompt={rememberDevicePrompt}
                  onRememberDevice={remember => {
                    if (remember) {
                      confirmTrustDevice({ history });
                    } else {
                      declineTrustDevice({ history });
                    }
                  }}
                  onOtpSubmit={otp => confirmOtp({ otp, history })}
                  onResend={() => resendOtp()}
                  userWithoutPhoneLogin={userWithoutPhoneLogin}
                  onBack={() => {
                    setOtpRequired(false);
                    clearError();
                    if (userWithoutPhoneLogin) {
                      setUserWithoutPhoneLogin(false);
                      setPhoneRequired(true);
                      setOtpRequired(false);
                    } else if (phoneRequired) {
                      setPhoneRequired(false);
                    }
                  }}
                  phoneRequired={phoneRequired}
                  onPhoneSubmit={async phoneNumber => {
                    const success = await updateAuthenticationProfile({
                      email: onSubmitValues.username,
                      phoneNumber,
                      userId: authenticationProfile.userId,
                      cognitoUsername: authenticationProfile.cognitoUsername,
                    });
                    if (success) {
                      setAuthenticationProfile({
                        ...authenticationProfile,
                        phoneNumber,
                      });
                      const loginSuccess = await login({
                        username: onSubmitValues.username,
                        password: onSubmitValues.password,
                        history,
                      });
                      if (loginSuccess) {
                        setOtpAttempts(0);
                        setUserWithoutPhoneLogin(true);
                        setPhoneRequired(false);
                        setOtpRequired(true);
                      }
                    }
                  }}
                  onSubmit={async values => {
                    const input = {
                      username: values.email.toLowerCase(),
                      password: values.password,
                      history,
                    };

                    setOnSubmitValues(input);
                    setLoading(true);
                    let profile;
                    try {
                      profile = await getAuthenticationProfile({
                        email: input.username,
                      });
                    } catch {
                      setLoading(false);
                      setError({
                        message:
                          'An unknown error has occurred. Please try again.',
                        error: true,
                      });
                      return;
                    }
                    setAuthenticationProfile(profile);
                    setLoading(false);

                    const shouldLogin =
                      profile.isServiceAccount ||
                      profile.isEmailEnabled ||
                      (profile.phoneNumberVerified && profile.phoneNumber);

                    if (!profile.userId) {
                      login({
                        username: values.email.toLowerCase(),
                        password: values.password,
                        history,
                      });
                    } else if (profile.userId && shouldLogin) {
                      login({
                        username: values.email.toLowerCase(),
                        password: values.password,
                        history,
                      });
                    } else {
                      clearError();
                      setPhoneRequired(true);
                      setOtpRequired(false);
                    }
                  }}
                />
              </FormCard>
            </div>
          </div>
        </div>
      </div>
      <div className="hero-foot">
        <FooterTextWrapper className="is-flex">
          <p className="is-size-4">
            @{new Date().getFullYear()} KraveIn All right reserved
          </p>
          <p className="is-size-4">
            <a>Term of use</a> & <a>Privacy policy</a>
          </p>
        </FooterTextWrapper>
      </div>
    </Container>
  );
};

export default Login;
